import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import ApiInspector from '../../../components/ApiInspector';
import ComboTabellata from '../../../components/ComboTabellata';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { API_DOCS } from './DispositiviAziendaliPage.api';

const MODULO = 'aziendale.hr.dispositivi';

// ── Icone ────────────────────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const LaptopIcon = ({ size = 48 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="2" y1="20" x2="22" y2="20" />
  </svg>
);

// ── Tipi ─────────────────────────────────────────────────────────────────────
interface TipologiaOption { id: number; nome: string; attiva: boolean; }
interface UbicazioneOption { id: number; nome: string; attiva: boolean; }

interface Dispositivo {
  id: number;
  societaId: number;
  codiceMatricola: string;
  tipologia: { id: number; nome: string };
  marca: string | null;
  modello: string | null;
  seriale: string | null;
  partNumber: string | null;
  proprieta: 'PROPRIETA' | 'TERZI';
  stato: 'NON_ASSEGNATO' | 'ASSEGNATO' | 'DISMESSO';
  ubicazioneCorrente: { id: number; nome: string } | null;
  assegnazioneAttiva: { id: number; dipendente: { id: number; nome: string; cognome: string } } | null;
}

interface FormNuovo {
  codiceMatricola: string;
  tipologiaId: number | null;
  proprieta: 'PROPRIETA' | 'TERZI';
  marca: string;
  modello: string;
  seriale: string;
  partNumber: string;
  ubicazioneId: number | null;
}

const STATO_BADGE: Record<string, string> = {
  NON_ASSEGNATO: 'bop-badge--neutral',
  ASSEGNATO: 'bop-badge--success',
  DISMESSO: 'bop-badge--danger',
};
const STATO_LABEL: Record<string, string> = {
  NON_ASSEGNATO: 'Non assegnato',
  ASSEGNATO: 'Assegnato',
  DISMESSO: 'Dismesso',
};

const FORM_EMPTY: FormNuovo = {
  codiceMatricola: '', tipologiaId: null, proprieta: 'PROPRIETA',
  marca: '', modello: '', seriale: '', partNumber: '', ubicazioneId: null,
};

export default function DispositiviAziendaliPage() {
  const navigate = useNavigate();
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');

  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const [dispositivi, setDispositivi] = useState<Dispositivo[]>([]);
  const [loading, setLoading] = useState(false);
  const [tipologie, setTipologie] = useState<TipologiaOption[]>([]);
  const [ubicazioni, setUbicazioni] = useState<UbicazioneOption[]>([]);

  // Filtri
  const [fTipologia, setFTipologia] = useState('');
  const [fStato, setFStato] = useState('');
  const [fProprieta, setFProprieta] = useState('');
  const [fUbicazione, setFUbicazione] = useState('');
  const [fQ, setFQ] = useState('');

  // Modale nuovo dispositivo
  const [showModalForm, setShowModalForm] = useState(false);
  const [formData, setFormData] = useState<FormNuovo>(FORM_EMPTY);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Load tabellate ─────────────────────────────────────────────────────────
  const loadTabellate = useCallback(async () => {
    if (!pronta) return;
    const p = new URLSearchParams();
    if (societaId) p.set('societaId', societaId);
    try {
      // @api GET /api/hr/tipologie-dispositivo?societaId -> {success:true,data:[{id,nome,attiva}]}
      const res = await apiFetch<{ success: boolean; data: TipologiaOption[] }>(`/api/hr/tipologie-dispositivo?${p}`);
      setTipologie(res.data);
    } catch { /* il filtro tipologia resta vuoto */ }
    try {
      // @api GET /api/hr/ubicazioni-dispositivo?societaId -> {success:true,data:[{id,nome,attiva,descrizione,sedeId,sede}]}
      const res = await apiFetch<{ success: boolean; data: UbicazioneOption[] }>(`/api/hr/ubicazioni-dispositivo?${p}`);
      setUbicazioni(res.data);
    } catch { /* il filtro ubicazione resta vuoto */ }
  }, [pronta, societaId]);

  // ── Load dispositivi ───────────────────────────────────────────────────────
  const loadDispositivi = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (societaId) p.set('societaId', societaId);
      if (fTipologia) p.set('tipologiaId', fTipologia);
      if (fStato) p.set('stato', fStato);
      if (fProprieta) p.set('proprieta', fProprieta);
      if (fUbicazione) p.set('ubicazioneId', fUbicazione);
      if (fQ) p.set('q', fQ);
      // @api GET /api/hr/dispositivi-aziendali?societaId&stato&tipologiaId&proprieta&ubicazioneId&q -> {success:true,data:[{id,societaId,codiceMatricola,tipologia,marca,modello,seriale,partNumber,proprieta,stato,ubicazioneCorrente,assegnazioneAttiva}]}
      const res = await apiFetch<{ success: boolean; data: Dispositivo[] }>(`/api/hr/dispositivi-aziendali?${p}`);
      setDispositivi(res.data);
    } catch (err: any) {
      showToast(err.message || 'Errore caricamento dispositivi', 'error');
    } finally {
      setLoading(false);
    }
  }, [pronta, societaId, fTipologia, fStato, fProprieta, fUbicazione, fQ]);

  useEffect(() => { loadTabellate(); }, [loadTabellate]);
  useEffect(() => { loadDispositivi(); }, [loadDispositivi]);

  // ── Creazione ──────────────────────────────────────────────────────────────
  const openCreate = () => {
    setFormData(FORM_EMPTY);
    setFormError('');
    setShowModalForm(true);
  };

  const handleSave = async () => {
    if (!formData.codiceMatricola.trim()) { setFormError('Codice matricola è obbligatorio'); return; }
    if (!formData.tipologiaId) { setFormError('Tipologia è obbligatoria'); return; }

    setSaving(true);
    setFormError('');
    try {
      const body = {
        societaId: societaId ? parseInt(societaId, 10) : undefined,
        codiceMatricola: formData.codiceMatricola.trim(),
        tipologiaId: formData.tipologiaId,
        proprieta: formData.proprieta,
        marca: formData.marca || null,
        modello: formData.modello || null,
        seriale: formData.seriale || null,
        partNumber: formData.partNumber || null,
        ubicazioneId: formData.ubicazioneId,
      };
      // @api POST /api/hr/dispositivi-aziendali {societaId,codiceMatricola,tipologiaId,proprieta,marca,modello,seriale,partNumber,ubicazioneId} -> {success:true,data:{id,codiceMatricola}}
      const res = await apiFetch<{ success: boolean; data: { id: number } }>('/api/hr/dispositivi-aziendali', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      showToast('Dispositivo creato', 'success');
      setShowModalForm(false);
      // Gli estremi di acquisto/ricezione si completano nella scheda
      navigate(`/gestione-aziendale/hr/dispositivi/${res.data.id}`);
    } catch (err: any) {
      setFormError(err.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // ── Dismetti ───────────────────────────────────────────────────────────────
  const handleDismetti = async (d: Dispositivo) => {
    try {
      // @api PATCH /api/hr/dispositivi-aziendali/:id/dismetti -> {success:true,data:{id,stato}}
      await apiFetch(`/api/hr/dispositivi-aziendali/${d.id}/dismetti`, { method: 'PATCH' });
      showToast(d.proprieta === 'TERZI' ? 'Dispositivo restituito al proprietario' : 'Dispositivo dismesso', 'success');
      loadDispositivi();
    } catch (err: any) {
      showToast(err.message || 'Errore nel dismettere', 'error');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="bop-disp-lista">

        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2 className="bop-page-title">Dispositivi Aziendali</h2>
          </div>
          {canEdit && (
            <button className="bop-btn bop-btn-primary" onClick={openCreate} disabled={!pronta}>
              <PlusIcon /> Nuovo Dispositivo
            </button>
          )}
        </div>

        <div className="bop-filter-bar bop-disp-lista__filtri">
          {!isUnica && (
            <select className="bop-select bop-disp-lista__filtro-societa" value={societaId}
              onChange={(e) => setSocietaId(e.target.value)}>
              <option value="">Tutte le società</option>
              {listaSocieta.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
            </select>
          )}
          <select className="bop-select bop-disp-lista__filtro" value={fTipologia} onChange={(e) => setFTipologia(e.target.value)}>
            <option value="">Tutte le tipologie</option>
            {tipologie.map((t) => (<option key={t.id} value={t.id}>{t.nome}</option>))}
          </select>
          <select className="bop-select bop-disp-lista__filtro" value={fUbicazione} onChange={(e) => setFUbicazione(e.target.value)}>
            <option value="">Tutte le ubicazioni</option>
            {ubicazioni.map((u) => (<option key={u.id} value={u.id}>{u.nome}</option>))}
          </select>
          <select className="bop-select bop-disp-lista__filtro" value={fProprieta} onChange={(e) => setFProprieta(e.target.value)}>
            <option value="">Proprietà e terzi</option>
            <option value="PROPRIETA">Di proprietà</option>
            <option value="TERZI">Di terzi</option>
          </select>
          <select className="bop-select bop-disp-lista__filtro" value={fStato} onChange={(e) => setFStato(e.target.value)}>
            <option value="">Tutti gli stati</option>
            <option value="NON_ASSEGNATO">Non assegnato</option>
            <option value="ASSEGNATO">Assegnato</option>
            <option value="DISMESSO">Dismesso</option>
          </select>
          <input className="bop-input bop-disp-lista__filtro-q"
            placeholder="Cerca matricola, marca, modello, seriale, part number…"
            value={fQ} onChange={(e) => setFQ(e.target.value)} />
        </div>

        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">
              <div className="bop-empty-state__icon"><LaptopIcon /></div>
              <p className="bop-empty-state__text">Seleziona una società per visualizzare i dispositivi</p>
            </div>
          </div>
        ) : (
          <div className="bop-card">
            <div className="bop-table-wrapper">
              {loading ? (
                <div className="inline-loading">Caricamento…</div>
              ) : dispositivi.length === 0 ? (
                <div className="bop-empty-state">
                  <div className="bop-empty-state__icon"><LaptopIcon /></div>
                  <h3 className="bop-empty-state__title">Nessun dispositivo</h3>
                  <p className="bop-empty-state__text">Nessun dispositivo trovato con i filtri selezionati</p>
                </div>
              ) : (
                <table className="bop-table bop-table--clickable">
                  <thead>
                    <tr>
                      <th>Matricola</th>
                      <th>Tipologia</th>
                      <th>Marca</th>
                      <th>Modello</th>
                      <th>Part number</th>
                      <th>Seriale</th>
                      <th>Provenienza</th>
                      <th>Ubicazione</th>
                      <th>Stato</th>
                      <th>Assegnato a</th>
                      <th className="bop-table__th--actions">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispositivi.map((d) => (
                      <tr key={d.id} onClick={() => navigate(`/gestione-aziendale/hr/dispositivi/${d.id}`)}>
                        <td className="bop-disp-lista__td-mono"><strong>{d.codiceMatricola}</strong></td>
                        <td>{d.tipologia?.nome}</td>
                        <td className="bop-text-muted">{d.marca || '—'}</td>
                        <td className="bop-text-muted">{d.modello || '—'}</td>
                        <td className="bop-text-muted bop-disp-lista__td-mono">{d.partNumber || '—'}</td>
                        <td className="bop-text-muted bop-disp-lista__td-mono">{d.seriale || '—'}</td>
                        <td>
                          <span className={`bop-badge ${d.proprieta === 'TERZI' ? 'bop-badge--warning' : 'bop-badge--info'}`}>
                            {d.proprieta === 'TERZI' ? 'Di terzi' : 'Di proprietà'}
                          </span>
                        </td>
                        <td className="bop-text-muted">{d.ubicazioneCorrente?.nome || '—'}</td>
                        <td>
                          <span className={`bop-badge ${STATO_BADGE[d.stato]}`}>{STATO_LABEL[d.stato]}</span>
                        </td>
                        <td>
                          {d.assegnazioneAttiva
                            ? `${d.assegnazioneAttiva.dipendente.cognome} ${d.assegnazioneAttiva.dipendente.nome}`
                            : <span className="bop-text-muted">—</span>}
                        </td>
                        <td className="bop-table__td--actions">
                          <BopRowActions actions={[
                            {
                              icon: <RowIcon.Disable />,
                              label: d.proprieta === 'TERZI' ? 'Restituisci al proprietario' : 'Dismetti',
                              onClick: () => handleDismetti(d),
                              variant: 'danger',
                              hidden: !canEdit || d.stato !== 'NON_ASSEGNATO',
                            },
                          ]} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ── Modale nuovo dispositivo ─────────────────────────────────────── */}
        {showModalForm && (
          <div className="bop-modal-overlay">
            <div className="bop-modal bop-disp-lista__modal">
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">Nuovo Dispositivo</h3>
                <button className="bop-btn-icon" onClick={() => setShowModalForm(false)}><XIcon /></button>
              </div>
              <div className="bop-modal__body">
                {formError && <div className="bop-inline-error">{formError}</div>}

                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Codice Matricola <span className="bop-text-danger">*</span></label>
                    <input className="bop-input" value={formData.codiceMatricola}
                      onChange={(e) => setFormData({ ...formData, codiceMatricola: e.target.value })}
                      placeholder="Es. PC-001" />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Tipologia <span className="bop-text-danger">*</span></label>
                    <ComboTabellata
                      label="tipologia"
                      value={formData.tipologiaId}
                      onChange={(id) => setFormData({ ...formData, tipologiaId: id })}
                      societaId={societaId ? parseInt(societaId, 10) : 0}
                      endpoint="/api/hr/tipologie-dispositivo"
                      disabled={!pronta}
                    />
                  </div>
                </div>

                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Proprietà</label>
                    <select className="bop-select" value={formData.proprieta}
                      onChange={(e) => setFormData({ ...formData, proprieta: e.target.value as 'PROPRIETA' | 'TERZI' })}>
                      <option value="PROPRIETA">Di proprietà dell'azienda</option>
                      <option value="TERZI">Di terzi (fornitore, cliente, partner)</option>
                    </select>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Ubicazione iniziale</label>
                    <ComboTabellata
                      label="ubicazione"
                      value={formData.ubicazioneId}
                      onChange={(id) => setFormData({ ...formData, ubicazioneId: id })}
                      societaId={societaId ? parseInt(societaId, 10) : 0}
                      endpoint="/api/hr/ubicazioni-dispositivo"
                      disabled={!pronta}
                    />
                  </div>
                </div>

                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Marca</label>
                    <input className="bop-input" value={formData.marca}
                      onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                      placeholder="Es. Apple, Dell, Samsung…" />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Modello</label>
                    <input className="bop-input" value={formData.modello}
                      onChange={(e) => setFormData({ ...formData, modello: e.target.value })}
                      placeholder="Es. MacBook Pro 14" />
                  </div>
                </div>

                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Seriale</label>
                    <input className="bop-input" value={formData.seriale}
                      onChange={(e) => setFormData({ ...formData, seriale: e.target.value })}
                      placeholder="Numero seriale…" />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Part number</label>
                    <input className="bop-input" value={formData.partNumber}
                      onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
                      placeholder="Es. MK183T/A" />
                  </div>
                </div>

                <p className="bop-text-muted bop-disp-lista__modal-nota">
                  Gli estremi di acquisto o di ricezione da terzi si compilano nella scheda del dispositivo.
                </p>
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setShowModalForm(false)} disabled={saving}>
                  Annulla
                </button>
                <button className="bop-btn bop-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Salvataggio…' : 'Salva e apri scheda'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
